/**
 * Janela de publicação: prévia produto a produto (ficha do catálogo, preço, margem, estoque e impedimentos),
 * escolha do tipo de anúncio e confirmação. Os anúncios são criados pausados.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ChannelSettingsResponse, ListingType, PublishBlock, PublishBody, PublishPreview, PublishResult } from '@berytus/contracts';
import { api, brl, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { titleOf } from './Catalog.tsx';

export const BLOCK: Record<PublishBlock, string> = {
  not_checked: 'EAN não consultado no catálogo',
  not_in_catalog: 'Sem ficha no catálogo do ML',
  parent_product: 'Ficha com variações (escolher a variação)',
  no_category: 'Categoria da ficha não identificada',
  no_gtin: 'Sem EAN',
  no_price: 'Sem preço',
  no_stock: 'Sem estoque para o canal',
  below_cost: 'Preço abaixo do custo',
  already_listed: 'Já existe anúncio para o SKU',
  pending: 'Publicação em andamento',
  trial_limit: 'Limite de anúncios do período de teste',
  kit_catalog: 'Kit: a ficha do catálogo é de 1 unidade (kit sai em anúncio próprio)',
  kit_no_photo: 'Kit sem foto própria: gere a foto do kit na aba Fotos',
  own_no_category: 'Sem ficha no ML: falta a categoria (Sem catálogo)',
  own_missing_attributes: 'Sem ficha no ML: faltam atributos (Sem catálogo)',
  own_no_photo: 'Sem ficha no ML: produto sem foto',
  controlled_344: 'Controlado (Portaria 344): falta habilitação do canal, aprovação da farmácia ou fluxo de receita',
  image_below_min: 'Imagem abaixo de 500 px (o ML recusa): envie uma foto maior',
  image_unchecked: 'Imagem sendo conferida para o ML: tente de novo em instantes',
  gtin_invalid: 'GTIN inválido: corrija o EAN no cadastro',
  channel_blocked: 'Bloqueado neste canal',
};

/** Alertas antes de anunciar (Produtos × anúncios e janela de publicação). */
export const PRE_ALERT: Record<string, { label: string; tone: string }> = {
  title: { label: 'Ficha diferente', tone: 'err' }, kit: { label: 'Kit × unidade', tone: 'err' }, measure: { label: 'Medida diferente', tone: 'err' },
  review: { label: 'Em revisão no ML', tone: 'warn' }, same_sku: { label: 'SKU já anunciado', tone: 'warn' }, pending: { label: 'Publicando', tone: 'info' },
  failed: { label: 'Recusado antes', tone: 'warn' }, below_cost: { label: 'Abaixo do custo', tone: 'err' }, low_margin: { label: 'Margem baixa', tone: 'warn' },
  default_price: { label: 'Preço padrão', tone: 'warn' }, no_stock: { label: 'Sem estoque', tone: 'off' },
};

const LISTING_STATUS: Record<string, string> = { active: 'ativo', paused: 'pausado', under_review: 'em revisão', closed: 'finalizado' };

const TYPE: Record<ListingType, string> = { gold_special: 'Clássico', gold_pro: 'Premium' };

export function PublishDialog({ accountId, target, onClose, onDone }: {
  accountId: string;
  target: Pick<PublishBody, 'productIds' | 'filter'>;
  onClose: () => void;
  onDone?: () => void;
}) {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ['meli', 'settings', accountId],
    queryFn: () => api<ChannelSettingsResponse>(`/channels/accounts/${accountId}/settings`),
  });
  const [type, setType] = useState<ListingType | null>(null);
  const listingType = type ?? settings.data?.settings.defaultListingType ?? 'gold_special';
  const [allowDup, setAllowDup] = useState(false);
  const preview = useQuery({
    queryKey: ['meli', 'publish-preview', accountId, JSON.stringify(target), listingType, allowDup],
    queryFn: () => api<PublishPreview>(`/channels/accounts/${accountId}/publish/preview`, { method: 'POST', json: { ...target, listingType, allowDuplicates: allowDup } }),
    enabled: !!settings.data,
  });
  const publish = useMutation({
    mutationFn: () => api<PublishResult>(`/channels/accounts/${accountId}/publish`, { method: 'POST', json: { ...target, listingType, allowDuplicates: allowDup } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meli'] });
      onDone?.();
    },
  });
  const items = preview.data?.items ?? [];
  const dupCount = items.filter((i) => i.blocks.includes('already_listed') || i.warnings.includes('already_listed')).length;
  const seller = preview.data?.seller;
  const blockedAccount = seller?.canList === false;
  const ready = blockedAccount ? 0 : preview.data?.ready ?? 0;

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content
          className="panel"
          style={{ position: 'fixed', top: '6vh', left: '50%', transform: 'translateX(-50%)', width: 'min(980px, calc(100vw - 32px))', maxHeight: '88vh', zIndex: 60, display: 'grid', gridTemplateRows: 'auto auto 1fr auto', gap: 14 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Dialog.Title asChild><h2 style={{ margin: 0 }}>Anunciar no Mercado Livre</h2></Dialog.Title>
            <span style={{ flex: 1 }} />
            <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
          </div>

          {publish.data ? (
            <div className="stack-y" style={{ gap: 10 }}>
              <div className="notice">
                <b>{int(publish.data.queued)}</b> anúncio(s) na fila de publicação{publish.data.skipped ? ` · ${int(publish.data.skipped)} ficaram de fora` : ''}.
                Eles são criados e pausados em seguida; acompanhe o resultado de cada um em Publicações.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Link className="btn primary" to="/canal/mercadolivre/publicacoes" onClick={onClose}>Ver publicações</Link>
                <button className="btn" type="button" onClick={onClose}>Fechar</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="seg" role="radiogroup" aria-label="Tipo de anúncio">
                  {(['gold_special', 'gold_pro'] as const).map((t) => (
                    <button key={t} type="button" aria-pressed={listingType === t} onClick={() => setType(t)}>{TYPE[t]}</button>
                  ))}
                </div>
                <span className="small muted">
                  Produtos com ficha: anúncio de catálogo (título, fotos e descrição da ficha do ML). Sem ficha: anúncio próprio, com a categoria e os atributos preparados em Sem catálogo. Kits: anúncio próprio, com a ficha técnica do produto base. Todos vão pausados; você ativa depois de conferir.
                </span>
              </div>

              {blockedAccount && (
                <div className="notice err" role="alert">
                  <b>O Mercado Livre não está aceitando anúncios desta conta.</b> {seller!.message}
                  {seller!.codes.length > 0 && <span className="small muted"> ({seller!.codes.join(', ')})</span>}
                  <div className="small" style={{ marginTop: 4 }}>Depois de acertar no Mercado Livre, aguarde alguns minutos e abra esta janela de novo.</div>
                </div>
              )}

              <div style={{ overflow: 'auto', border: '1px solid var(--line)', borderRadius: 'var(--radius)' }}>
                {preview.isPending ? <Loading /> : preview.error ? <div style={{ padding: 12 }}><ErrorBox error={preview.error} /></div> : (
                  <table className="simple">
                    <thead>
                      <tr><th>Produto</th><th>Ficha no catálogo</th><th className="num">Preço</th><th className="num">Margem</th><th className="num">Estoque</th><th>Situação</th></tr>
                    </thead>
                    <tbody>
                      {items.map((i) => (
                        <tr key={i.productId} style={i.blocks.length ? { opacity: 0.6 } : undefined}>
                          <td><div style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.title ?? undefined}>{titleOf(i)}</div><span className="small muted">SKU {i.sku}</span></td>
                          <td>
                            {i.mode === 'own' ? (
                              <><Tag tone="info">Anúncio próprio · sem ficha</Tag><div className="small muted" style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.categoryPath ?? undefined}>{i.categoryPath ?? 'categoria não definida'}</div>
                                {i.missing && i.missing.length > 0 && <div className="small" style={{ color: 'var(--warn)' }}>falta: {i.missing.join(', ')}</div>}</>
                            ) : i.mode === 'listing' ? (
                              <><Tag tone="info">Anúncio próprio · kit {i.kitUnits} un.</Tag><div className="small muted" style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.catalogName ?? undefined}>ficha técnica de: {i.catalogName ?? '—'}</div></>
                            ) : (
                              <><div style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.catalogName ?? undefined}>{i.catalogName ?? '—'}</div><span className="small muted">{i.catalogProductId ?? ''}</span></>
                            )}
                          </td>
                          <td className="num">{brl(i.price)}</td>
                          <td className="num" style={i.warnings.includes('low_margin') ? { color: 'var(--err)' } : undefined}>{i.marginPct == null ? '—' : `${i.marginPct.toLocaleString('pt-BR')}%`}</td>
                          <td className="num">{int(i.quantity)}</td>
                          <td>
                            {i.blocks.length === 0 ? <Tag tone="ok">Pronto</Tag> : i.blocks.map((b) => <div key={b}><Tag tone="off">{BLOCK[b]}</Tag></div>)}
                            {i.warnings.includes('low_margin') && <div><Tag tone="warn">Margem baixa</Tag></div>}
                            {i.warnings.includes('default_price') && <div><Tag tone="warn">Preço padrão</Tag></div>}
                            {i.warnings.includes('already_listed') && <div><Tag tone="warn">Duplicado: já tem anúncio</Tag></div>}
                            {i.warnings.includes('no_package') && <div title="Sem peso e medidas no produto nem no catálogo mestre: vai com 0,5 cm / 0,5 kg. Preencha na aba Logística do produto."><Tag tone="warn">Medidas padrão (0,5)</Tag></div>}
                            {i.alerts?.map((a) => <div key={a.code} title={a.detail}><Tag tone={PRE_ALERT[a.code]?.tone ?? 'warn'}>{PRE_ALERT[a.code]?.label ?? a.code}</Tag></div>)}
                            {i.existing?.map((e) => (
                              <div key={e.externalId} className="small muted" style={{ whiteSpace: 'nowrap' }}>
                                {e.externalId} · {LISTING_STATUS[e.status ?? ''] ?? e.status}{e.catalog ? ' · catálogo' : ''}{e.sku && e.sku !== i.sku ? ` · SKU ${e.sku}` : ''}
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {dupCount > 0 && (
                <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--warn-bg)' }}>
                  <input type="checkbox" className="check" checked={allowDup} onChange={(e) => setAllowDup(e.target.checked)} />
                  <span>
                    <b>{int(dupCount)} produto(s) já têm anúncio nesta conta</b> (veja o número e a situação na lista).
                    {' '}Marque para criar mesmo assim — um anúncio a mais para o mesmo SKU.
                  </span>
                </label>
              )}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn primary" type="button" disabled={!ready || publish.isPending} onClick={() => publish.mutate()}>
                  {publish.isPending ? 'Enviando…' : `Publicar ${int(ready)} anúncio(s) pausado(s)`}
                </button>
                <button className="btn" type="button" onClick={onClose}>Cancelar</button>
                {preview.data && (
                  <span className="small muted">
                    {int(ready)} pronto(s) · {int(preview.data.blocked)} com impedimento{preview.data.truncated ? ' · limite de 2.000 por vez' : ''} · {TYPE[listingType]}
                  </span>
                )}
              </div>
              {publish.error && <ErrorBox error={publish.error} />}
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
